import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Wizard from '../containers/Modal/Wizard';
import { Input, SelectField } from '../containers/Form';
import Strings from '../Strings';
import { closeModal } from '../actions/modal';
import { actions as orgActions } from '../pages/Organizations/redux/actions';
import { notificationActions } from '../components/Notification/redux/actions';
import { SAVE_ORG_TEMPLATE_ERROR } from '../pages/Organizations/redux/constants';
import RadioButtons from '../components/RadioButtons/RadioButtons';
import { isSuperUser } from '../utils/userRoles';

export const CREATE_ORG_TEMPLATE_MODAL_NAME = 'create-organization-template';

function CreateOrganizationTemplateModal(props) {
  const {
    onCancel,
    getOrganizations,
    createOrgTemplate,
    saveOrgTemplate,
    getOrgTemplates,
    showNotification,
  } = props;

  const isSuperUserRole = isSuperUser();
  const [createMode, setCreateMode] = React.useState('empty');
  const [organizationId, setOrganizationId] = React.useState('');
  const [name, setName] = React.useState('');
  const [ownerUuid, setOwnerUuid] = React.useState('');
  const [organizations, setOrganizations] = React.useState([]);
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    getOrganizations({ limit: 1000, offset: 0 }).then(resp => {
      const raw = Array.isArray(resp?.response)
        ? resp.response
        : resp?.response?.data || [];
      const options = raw.map(org => {
        const value = org?.uuid || org?.masked_id || org?.id;
        const label = org?.name || String(value || '');
        return { value, label };
      }).filter(option => option.value);
      setOrganizations(options);
    });
  }, [getOrganizations]);

  React.useEffect(() => {
    const errors = [];
    if (!name.trim()) {
      errors.push({ property: 'name', propertyName: Strings.name, missing: true });
    }
    if (!isSuperUserRole && !ownerUuid) {
      errors.push({ property: 'owner', propertyName: Strings.owner, missing: true });
    }
    if (createMode === 'organization' && !organizationId) {
      errors.push({ property: 'organization', propertyName: Strings.organization, missing: true });
    }
    setValidationErrors(errors);
  }, [createMode, name, organizationId, ownerUuid, isSuperUserRole]);

  const onSubmit = () => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);

    const templateName = name.trim();
    const ownerField = ownerUuid ? { owner_uuid: ownerUuid } : { owner_uuid: null };

    const savePromise = createMode === 'organization'
      ? saveOrgTemplate(organizationId, templateName, ownerUuid || null)
      : createOrgTemplate({ name: templateName, ...ownerField });

    savePromise.then(resp => {
      if (resp?.type === SAVE_ORG_TEMPLATE_ERROR) {
        showNotification(resp?.response?.data?.error?.message || Strings.errors?.unknownError, 5000, true);
        setIsSaving(false);
        return;
      }
      showNotification(Strings.templateSaved);
      getOrgTemplates();
      onCancel();
    });
  };

  const ownerOptions = [{ value: '', label: Strings.none }].concat(organizations);

  const pages = [
    {
      id: 'create-org-template-page',
      title: Strings.createTemplate,
      content: (
        <>
          <RadioButtons
            key={`create-template-mode-${createMode}`}
            title={Strings.templateSource}
            initSelectedValue={createMode}
            onClick={value => {
              setCreateMode(value);
              if (value === 'empty') {
                setOrganizationId('');
              }
            }}
            buttons={[
              { label: Strings.createEmptyTemplate, value: 'empty' },
              { label: Strings.createTemplateFromOrganization, value: 'organization' },
            ]}
          />
          {createMode === 'organization' && (
            <SelectField
              name="sourceOrganization"
              id="sourceOrganization"
              label={Strings.organization}
              value={organizationId}
              onChange={option => setOrganizationId(option?.value || '')}
              data={organizations}
              isSearchable
              searchByLabelOnly
              isRequired
              highlightInvalid={highlightInvalidFields}
              errorsForTooltip={validationErrors.find(v => v.property === 'organization')?.errors || []}
              data-testid="template-source-organization"
            />
          )}
          <Input
            name="templateName"
            id="templateName"
            label={Strings.name}
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            isRequired
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={validationErrors.find(v => v.property === 'name')?.errors || []}
            data-testid="template-name"
          />
          <SelectField
            name="templateOwner"
            id="templateOwner"
            label={Strings.owner}
            value={ownerUuid}
            onChange={option => setOwnerUuid(option?.value || '')}
            data={ownerOptions}
            isSearchable
            searchByLabelOnly
            isRequired={!isSuperUserRole}
            highlightInvalid={highlightInvalidFields}
            errorsForTooltip={validationErrors.find(v => v.property === 'owner')?.errors || []}
            data-testid="template-owner"
          />
        </>
      ),
      emptyFieldsCount: validationErrors.filter(v => v.missing).length,
      canGoNext: validationErrors.length === 0 && !isSaving,
      nextButton: { text: Strings.save },
    },
  ];

  return (
    <Wizard
      name={CREATE_ORG_TEMPLATE_MODAL_NAME}
      pages={pages}
      onSubmit={onSubmit}
      onNextButtonHover={e => setHighlightMissingFields(e)}
      showPagesFilter={false}
    />
  );
}

CreateOrganizationTemplateModal.propTypes = {
  onCancel: PropTypes.func,
  getOrganizations: PropTypes.func,
  createOrgTemplate: PropTypes.func,
  saveOrgTemplate: PropTypes.func,
  getOrgTemplates: PropTypes.func,
  showNotification: PropTypes.func,
};

const mapDispatchToProps = dispatch => ({
  onCancel: () => dispatch(closeModal(CREATE_ORG_TEMPLATE_MODAL_NAME)),
  getOrganizations: pageRequest => dispatch(orgActions.queryOrganizations(pageRequest)),
  createOrgTemplate: data => dispatch(orgActions.createOrgTemplate(data)),
  saveOrgTemplate: (organizationId, name, ownerUuid) =>
    dispatch(orgActions.saveOrgTemplate(organizationId, name, ownerUuid)),
  getOrgTemplates: () => dispatch(orgActions.getOrgTemplates()),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
});

export default connect(null, mapDispatchToProps)(CreateOrganizationTemplateModal);
